import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { isDailyContract } from 'modules/timeoff/lib/contractsVersion'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface Props {
  form: LeaveForm
  leave?: Leave | null | undefined
  leaveTypes: LeaveType[]
  employee?: Employee | null | undefined
  readOnly: boolean
  hasMedicalLeavesOpen: (employeeId: number) => boolean
  leaveTypeDescription?: string
}

const Types = ({
  form,
  leave,
  leaveTypes,
  employee,
  readOnly,
  hasMedicalLeavesOpen,
  leaveTypeDescription,
}: Props) => {
  const { contract } = useContractsVersion()
  const field = form.get('leave_type_id')
  const leaveType = leaveTypes.find((type) => type.attributes.id === parseInt(field.value))

  const sortedLeaveType = (leaveType: LeaveType): string => {
    if (leaveType.isHoliday) return '0'

    if (leaveType.isMedicalLeave) return '1'

    return `3${leaveType.name}`
  }

  /**
   * Employee only can have an open medical leave
   * In case we detect that we disabled medical_leave type
   * in selector
   * Open means: a medical leave that finish today or after and
   * is approved
   */
  const isDisabled = useUnsafeCallback(
    (leaveType: LeaveType): boolean => {
      const archived = !leaveType.attributes.active

      if (archived) return true

      if (!employee || !leaveType.isMedicalLeave) return false

      return hasMedicalLeavesOpen(employee.attributes.id)
    },
    [employee, hasMedicalLeavesOpen]
  )

  const leaveTypeCopy = (leaveType: LeaveType): string => {
    const accrues = leaveType.attributes.accrues
      ? i18n.t('settings.leave_types.attributes_info.accrues')
      : i18n.t('settings.leave_types.attributes_info.does_not_accrues')

    const approval = leaveType.attributes.approval_required
      ? i18n.t('settings.leave_types.attributes_info.approval')
      : i18n.t('settings.leave_types.attributes_info.no_approval')

    return i18n.t('settings.leave_types.attributes_info.sentence', {
      leave_type: leaveType.name,
      accrues,
      approval,
    })
  }

  const options = useUnsafeMemo(() => {
    let filteredLeaveTypes

    if (isDailyContract(contract) && employee) {
      filteredLeaveTypes = leaveTypes.filter(
        (leaveType) => !leaveType.isHourlyForEmployee(employee)
      )
    } else {
      filteredLeaveTypes = leaveTypes
    }

    return sortBy(filteredLeaveTypes, sortedLeaveType).map((leaveType) => {
      const disabled = isDisabled(leaveType)

      const label =
        disabled && leaveType.isMedicalLeave
          ? i18n.t('time_off.medical_leave.disabled_because_open')
          : leaveTypeCopy(leaveType)

      return {
        value: leaveType.attributes.id,
        label,
        disabled,
      }
    })
  }, [employee, isDisabled, leaveTypes])

  const label = i18n.t('time_off.type')
  const value = leaveType ? leaveTypeCopy(leaveType) : '-'

  if (!leaveType && !!leave?.attributes.medical_leave_id) {
    return null
  }

  if (readOnly) {
    return (
      <FormRow>
        <Box gap="s8" paddingTop="s12">
          <TextField readonly label={label} value={value} />
          {leaveTypeDescription && (
            <Text size="100" weight="normal">
              {leaveTypeDescription}
            </Text>
          )}
        </Box>
      </FormRow>
    )
  }

  return (
    <FormRow>
      <Box gap="s8" width="s400" paddingTop="s12">
        <Select label={label} field={field} options={options} showOriginalLabelText />
        {leaveTypeDescription && (
          <Text size="100" weight="normal">
            {leaveTypeDescription}
          </Text>
        )}
      </Box>
    </FormRow>
  )
}

export default observer(Types)
